import { CUSTOM_ELEMENTS_SCHEMA, NgModule, inject, provideAppInitializer } from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {HeaderComponent} from './layout/header/header.component';
import {LandingLayoutComponent} from './layout/landing-layout/landing-layout.component';
import {ApiReferenceLayoutComponent} from './layout/api-reference-layout/api-reference-layout.component';
import {NavbarComponent} from './layout/navbar/navbar.component';
import {LandingPageComponent} from './pages/landing-page/landing-page.component';
import {LandingProductPageComponent} from './pages/landing-product-page/landing-product-page.component';

import {
  HighlightModule,
  HIGHLIGHT_OPTIONS,
  HighlightOptions,
} from 'ngx-highlightjs';

import {MdbAccordionModule} from 'mdb-angular-ui-kit/accordion';
import {MdbCarouselModule} from 'mdb-angular-ui-kit/carousel';
import {MdbCheckboxModule} from 'mdb-angular-ui-kit/checkbox';
import {MdbCollapseModule} from 'mdb-angular-ui-kit/collapse';
import {MdbDropdownModule} from 'mdb-angular-ui-kit/dropdown';
import {MdbFormsModule} from 'mdb-angular-ui-kit/forms';
import {MdbModalModule} from 'mdb-angular-ui-kit/modal';
import {MdbPopoverModule} from 'mdb-angular-ui-kit/popover';
import {MdbRadioModule} from 'mdb-angular-ui-kit/radio';
import {MdbRangeModule} from 'mdb-angular-ui-kit/range';
import {MdbRippleModule} from 'mdb-angular-ui-kit/ripple';
import {MdbScrollspyModule} from 'mdb-angular-ui-kit/scrollspy';
import {MdbTabsModule} from 'mdb-angular-ui-kit/tabs';
import {MdbTooltipModule} from 'mdb-angular-ui-kit/tooltip';
import {MdbValidationModule} from 'mdb-angular-ui-kit/validation';
import {LandingPageCardComponent} from './components/util/landing-page-card/landing-page-card.component';
import {LoginPageComponent} from './pages/login-page/login-page.component';
import {LoginLayoutComponent} from './layout/login-layout/login-layout.component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DashboardLayoutComponent} from './layout/dashboard-layout/dashboard-layout.component';
import {RegistrationPageComponent} from './pages/registration-page/registration-page.component';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  BrokerEditModalComponent,
  BrokerUserDetailsModalComponent, ConfirmationModalComponent,
  ManagementPageComponent, ResetPasswordModalComponent
} from './pages/management-page/management-page.component';

import {CommonModule} from '@angular/common';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {ToastrModule} from 'ngx-toastr';
import {authInterceptorProviders} from './helpers/auth.interceptor';
import {NgxPaginationModule} from 'ngx-pagination';
import {ProfilePageComponent} from "./pages/profile-page/profile-page.component";
import {FormInputFieldComponent} from './components/util/form-input-field/form-input-field.component';
import {LandingPageGoalsComponent} from './components/util/landing-page-goals/landing-page-goals.component';
import {AppConfigService} from "./services/config/app-config.service";
import {WebsocketClientComponent} from './components/util/websocket-client/websocket-client.component';
import {WebsocketService} from "./services/websocket/websocket.service";
import {FooterComponent} from "./layout/footer/footer.component";
import {RouterModule} from "@angular/router";
import {RestApiComponent} from './components/stoplight-apis/rest-api/rest-api.component';
import {
  ApproveRequestModalComponent,
  PendingRequestComponent,
  PendingUserDetailsModalComponent
} from './pages/pending-request-page/pending-request-page.component';
import {ChangePasswordPageComponent} from './pages/change-password-page/change-password-page.component';
import {PasswordStrengthBarComponent} from './components/util/password-strength-bar/password-strength-bar.component';
import {NgxIntlTelInputModule} from "ngx-intl-tel-input";
import {ForgotPasswordPageComponent} from './pages/forgot-password-page/forgot-password-page.component';
import {FixApiComponent} from './components/stoplight-apis/fix-api/fix-api.component';
import {ExtrenalApiComponent} from './components/stoplight-apis/extrenal-api/extrenal-api.component';
import {FeedApiComponent} from "./components/stoplight-apis/feed-api/feed-api.component";
import { ConfigurationsPageComponent } from './pages/configurations-page/configurations-page.component';
import { ConfigurationsPageMainComponent } from './pages/configurations-page-main/configurations-page-main.component';
import { InstDetailsModalComponent } from './pages/configurations-page/configurations-page.component';
import { InstDetailsModalDIFCComponent } from './pages/configurations-page/configurations-page.component';
import { ConfigurationsPageComponent2Component } from './pages/configurations-page-component2/configurations-page-component2.component';
import { AppKeySecretPageComponent } from './pages/app-key-secret-page/app-key-secret-page.component';
import { AddInstitutionComponent } from './pages/add-institution/add-institution.component';
import { AddInstitutionMainComponent } from './pages/add-institution-main/add-institution-main.component';
import { AddBankComponent } from './pages/add-bank/add-bank.component';
import { AddBankAccountComponent } from './pages/add-bank-account/add-bank-account.component';
import { AddExchangeComponent } from './pages/add-exchange/add-exchange.component';
import { CreateMasterAccountComponent } from './pages/create-master-account/create-master-account.component';
import { MasterAccountsComponent } from './pages/master-accounts/master-accounts.component';
import { SummaryCreateInstitutionComponent } from './pages/summary-create-institution/summary-create-institution.component';
import { TradingLimitsInstitutionComponent } from './pages/trading-limits-institution/trading-limits-institution.component';
import { AccountViewComponent } from './pages/customer-creation/account-view.component';
import { CustomerCreationComponent,CustomerCreationModalComponent,SubscriptionPackCreationModalComponent} from './pages/customer-creation/customer-creation.component';
import { AddBankAccountModalComponent } from './pages/add-bank-account/add-bank-account.component';
import { NgWizardModule, NgWizardConfig, THEME } from 'ng-wizard3';
import {KeycloakService} from "keycloak-angular";
import {initializeKeycloak} from "./services/keycloak-init.factory";

import { Ng2GoogleChartsModule } from 'ng2-google-charts';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { NgSelectModule } from '@ng-select/ng-select';
import { MonitoringDashboardComponent } from './pages/monitoring-dashboard/monitoring-dashboard.component';
import { RECAPTCHA_SETTINGS, RecaptchaFormsModule, RecaptchaModule, RecaptchaSettings } from 'ng-recaptcha';
import { MicroInvestingApiComponent } from './components/stoplight-apis/micro-investing-api/micro-investing-api.component';
import { InvestApiComponent } from './components/stoplight-apis/invest-api/invest-api.component';
import { MaintenanceNotificationComponent } from './components/util/maintenance-notification/maintenance-notification.component';
import { MaintenanceSchedulingComponent } from './pages/maintenance-scheduling/maintenance-scheduling.component';
import { MaintenanceService } from './services/maintenance.service';
import { GeneratePresignedUrlComponent } from './pages/generate-presigned-url/generate-presigned-url.component';
import { InstConfigsComponent } from './pages/inst-configs/inst-configs.component';
import { KeyGenAppLayoutComponent } from './layout/key-gen-app-layout/key-gen-app-layout.component';
import { GenerateKeysComponent } from './pages/generate-keys/generate-keys.component';
import { GenerateAssertionComponent } from './pages/generate-assertion/generate-assertion.component';
import { AssertionGenAppLayoutComponent } from './layout/assertion-gen-app-layout/assertion-gen-app-layout.component';
import { Base64EncoderComponent } from './pages/base64-encoder/base64-encoder.component';
import { Base64EncoderAppLayoutComponent } from './layout/base64-encoder-app-layout/base64-encoder-app-layout.component';
import { UploadZipComponent } from './pages/upload-zip/upload-zip.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { ForbiddenPageComponent } from './pages/forbidden-page/forbidden-page.component';

const ngWizardConfig: NgWizardConfig = {
  theme: THEME.default
};
export function commonConfig(jsonLoader: AppConfigService): () => Promise<any> {
  return () => jsonLoader.loadAppConfig();
}

export function asiaConfig(jsonLoader: AppConfigService): () => Promise<any> {
  return () => jsonLoader.loadAsiaConfig();
}

export function menaConfig(jsonLoader: AppConfigService): () => Promise<any> {
  return () => jsonLoader.loadMenaConfig();
}


@NgModule({ schemas: [CUSTOM_ELEMENTS_SCHEMA],
    declarations: [
        AppComponent,
        HeaderComponent,
        LandingLayoutComponent,
        ApiReferenceLayoutComponent,
        NavbarComponent,
        LandingPageComponent,
        LandingProductPageComponent,
        LandingPageCardComponent,
        LoginPageComponent,
        LoginLayoutComponent,
        DashboardLayoutComponent,
        RegistrationPageComponent,
        ManagementPageComponent,
        PendingRequestComponent,
        ProfilePageComponent,
        FormInputFieldComponent,
        LandingPageGoalsComponent,
        NavbarComponent,
        RestApiComponent,
        WebsocketClientComponent,
        FooterComponent,
        ApproveRequestModalComponent,
        ChangePasswordPageComponent,
        ChangePasswordPageComponent,
        PasswordStrengthBarComponent,
        BrokerEditModalComponent,
        BrokerUserDetailsModalComponent,
        PendingUserDetailsModalComponent,
        ForgotPasswordPageComponent,
        FixApiComponent,
        ExtrenalApiComponent,
        FeedApiComponent,
        ConfigurationsPageComponent,
        ConfigurationsPageMainComponent,
        InstDetailsModalComponent,
        InstDetailsModalDIFCComponent,
        ConfigurationsPageComponent2Component,
        AppKeySecretPageComponent,
        AddInstitutionComponent,
        AddInstitutionMainComponent,
        AddBankComponent,
        AddBankAccountComponent,
        AddExchangeComponent,
        CreateMasterAccountComponent,
        MasterAccountsComponent,
        SummaryCreateInstitutionComponent,
        TradingLimitsInstitutionComponent,
        CustomerCreationComponent,
        AccountViewComponent,
        CustomerCreationModalComponent,
        SubscriptionPackCreationModalComponent,
        AddBankAccountModalComponent,
        ConfirmationModalComponent,
        MonitoringDashboardComponent,
        ResetPasswordModalComponent,
        MicroInvestingApiComponent,
        InvestApiComponent,
        MaintenanceNotificationComponent,
        MaintenanceSchedulingComponent,
        GeneratePresignedUrlComponent,
        InstConfigsComponent,
        KeyGenAppLayoutComponent,
        GenerateKeysComponent,
        GenerateAssertionComponent,
        AssertionGenAppLayoutComponent,
        Base64EncoderComponent,
        Base64EncoderAppLayoutComponent,
        UploadZipComponent,
        NotFoundPageComponent,
        ForbiddenPageComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        MdbAccordionModule,
        MdbCarouselModule,
        MdbCheckboxModule,
        MdbCollapseModule,
        MdbDropdownModule,
        MdbFormsModule,
        MdbModalModule,
        MdbPopoverModule,
        MdbRadioModule,
        MdbRangeModule,
        MdbRippleModule,
        MdbScrollspyModule,
        MdbTabsModule,
        MdbTooltipModule,
        MdbValidationModule,
        BrowserModule,
        AppRoutingModule,
        ReactiveFormsModule,
        CommonModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            timeOut: 3000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: true,
            closeButton: false,
            tapToDismiss: true
        }),
        NgxPaginationModule,
        FormsModule,
        HighlightModule,
        RouterModule,
        NgxIntlTelInputModule,
        NgWizardModule.forRoot(ngWizardConfig),
        Ng2GoogleChartsModule,
        BsDropdownModule.forRoot(),
        NgSelectModule,
        RecaptchaModule,
        RecaptchaFormsModule], providers: [
        authInterceptorProviders,
        AppConfigService,
        WebsocketService,
        MaintenanceService,
        ManagementPageComponent,
        AddInstitutionComponent,
        CustomerCreationComponent,
        KeycloakService,
        provideAppInitializer(() => {
        const initializerFn = (initializeKeycloak)(inject(KeycloakService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (commonConfig)(inject(AppConfigService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (asiaConfig)(inject(AppConfigService));
        return initializerFn();
      }),
        provideAppInitializer(() => {
        const initializerFn = (menaConfig)(inject(AppConfigService));
        return initializerFn();
      }),
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: <HighlightOptions>{
                lineNumbers: true,
                coreLibraryLoader: () => import('highlight.js/lib/core'),
                themePath: 'node_modules/highlight.js/styles/github.css',
                languages: {
                    typescript: () => import('highlight.js/lib/languages/typescript'),
                    css: () => import('highlight.js/lib/languages/css'),
                    xml: () => import('highlight.js/lib/languages/xml'),
                },
            },
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }

